/**
 * Coverage Dashboard Card — Visual display of version/test plan coverage metrics.
 *
 * Renders coverage data from get_version_coverage, get_test_plan_progress,
 * and get_version_release_readiness tools as an interactive dashboard card.
 */

import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Assessment as CoverageIcon,
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  Block as BlockedIcon,
  SkipNext as SkippedIcon,
  PlayCircleOutline as PendingIcon,
  Shield as ReadinessIcon,
} from '@mui/icons-material';

interface CoverageDashboardCardProps {
  tool: string;
  data: Record<string, any>;
}

/** Single metric row with label + value + optional bar. */
const MetricRow: React.FC<{
  label: string;
  value: string | number;
  color?: string;
  total?: number;
  current?: number;
}> = ({ label, value, color, total, current }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
    <Typography variant="caption" sx={{ minWidth: 120, color: 'text.secondary' }}>
      {label}
    </Typography>
    {total != null && current != null ? (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress
          variant="determinate"
          value={total > 0 ? (current / total) * 100 : 0}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': { bgcolor: color || 'primary.main', borderRadius: 3 },
          }}
        />
        <Typography variant="caption" fontWeight={600} sx={{ minWidth: 50, textAlign: 'right' }}>
          {value}
        </Typography>
      </Box>
    ) : (
      <Typography variant="caption" fontWeight={600} sx={{ color: color || 'text.primary' }}>
        {value}
      </Typography>
    )}
  </Box>
);

/** Status chip with icon and color. */
const StatusChip: React.FC<{ label: string; count: number; color: string; icon: React.ReactElement }> = ({
  label,
  count,
  color,
  icon,
}) => (
  <Chip
    icon={icon}
    label={`${count} ${label}`}
    size="small"
    sx={{
      bgcolor: `${color}15`,
      color,
      fontWeight: 600,
      fontSize: '0.7rem',
      '& .MuiChip-icon': { color },
    }}
  />
);

/** Circular coverage gauge using SVG. */
const CoverageGauge: React.FC<{ percentage: number; label: string }> = ({ percentage, label }) => {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const color =
    percentage >= 80 ? '#2e7d32' : percentage >= 50 ? '#ed6c02' : percentage > 0 ? '#d32f2f' : '#9e9e9e';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <svg width="76" height="76" viewBox="0 0 76 76">
        <circle cx="38" cy="38" r={radius} fill="none" stroke="#e0e0e0" strokeWidth="6" />
        <circle
          cx="38"
          cy="38"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 38 38)"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
        <text x="38" y="38" textAnchor="middle" dominantBaseline="central" fontSize="16" fontWeight="700" fill={color}>
          {percentage}%
        </text>
      </svg>
      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
        {label}
      </Typography>
    </Box>
  );
};

const CoverageDashboardCard: React.FC<CoverageDashboardCardProps> = ({ tool, data }) => {
  if (tool === 'get_version_coverage') {
    return <VersionCoverageCard data={data} />;
  }
  if (tool === 'get_test_plan_progress') {
    return <TestPlanProgressCard data={data} />;
  }
  if (tool === 'get_version_release_readiness') {
    return <ReleaseReadinessCard data={data} />;
  }
  return null;
};

/** Version coverage dashboard. */
const VersionCoverageCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const coverage = data.coverage || {};
  const reqCov = coverage.requirements_coverage || {};
  const execCov = coverage.execution_coverage || {};
  const totalReqs = reqCov.total_requirements ?? 0;
  const coveredReqs = reqCov.covered_requirements ?? 0;
  const reqPct = reqCov.coverage_percentage ?? 0;
  const totalCases = execCov.total_cases ?? 0;
  const executed = execCov.executed ?? 0;
  const passed = execCov.passed ?? 0;
  const failed = execCov.failed ?? 0;
  const blocked = execCov.blocked ?? 0;
  const skipped = execCov.skipped ?? 0;
  const execPct = execCov.execution_percentage ?? 0;
  const passPct = execCov.pass_rate ?? 0;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'info.200',
        borderRadius: 2,
        p: 2,
        my: 1,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CoverageIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={700}>
          Version Coverage Dashboard
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, justifyContent: 'center' }}>
        <CoverageGauge percentage={Math.round(reqPct)} label="Requirement Coverage" />
        <CoverageGauge percentage={Math.round(execPct)} label="Execution Coverage" />
        <CoverageGauge percentage={Math.round(passPct)} label="Pass Rate" />
      </Box>

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Requirements
      </Typography>
      <MetricRow label="Total" value={totalReqs} />
      <MetricRow label="Covered" value={`${coveredReqs}/${totalReqs}`} color="#2e7d32" total={totalReqs} current={coveredReqs} />

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Test Execution
      </Typography>
      <MetricRow label="Total Cases" value={totalCases} />
      <MetricRow label="Executed" value={`${executed}/${totalCases}`} color="#1976d2" total={totalCases} current={executed} />

      {totalCases > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          <StatusChip label="Passed" count={passed} color="#2e7d32" icon={<PassedIcon sx={{ fontSize: 14 }} />} />
          <StatusChip label="Failed" count={failed} color="#d32f2f" icon={<FailedIcon sx={{ fontSize: 14 }} />} />
          <StatusChip label="Blocked" count={blocked} color="#ed6c02" icon={<BlockedIcon sx={{ fontSize: 14 }} />} />
          <StatusChip label="Skipped" count={skipped} color="#9e9e9e" icon={<SkippedIcon sx={{ fontSize: 14 }} />} />
        </Box>
      )}
    </Box>
  );
};

/** Test plan progress dashboard. */
const TestPlanProgressCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const progress = data.progress || data;
  const status = progress.status || 'UNKNOWN';
  const totalCases = progress.total_cases ?? 0;
  const executed = progress.executed ?? 0;
  const passed = progress.passed ?? 0;
  const failed = progress.failed ?? 0;
  const blocked = progress.blocked ?? 0;
  const skipped = progress.skipped ?? 0;
  const pending = progress.pending ?? (totalCases - executed);
  const executionPct = totalCases > 0 ? Math.round((executed / totalCases) * 100) : 0;
  const passPct = executed > 0 ? Math.round((passed / executed) * 100) : 0;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'primary.200',
        borderRadius: 2,
        p: 2,
        my: 1,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CoverageIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={700}>
            Test Plan Progress
          </Typography>
        </Box>
        <Chip label={status} size="small" color={status === 'COMPLETED' ? 'success' : status === 'IN_PROGRESS' ? 'primary' : 'default'} />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, justifyContent: 'center' }}>
        <CoverageGauge percentage={executionPct} label="Execution" />
        <CoverageGauge percentage={passPct} label="Pass Rate" />
      </Box>

      <Divider sx={{ my: 1 }} />

      <MetricRow label="Total Cases" value={totalCases} />
      <MetricRow label="Executed" value={`${executed}/${totalCases}`} color="#1976d2" total={totalCases} current={executed} />
      <MetricRow label="Pending" value={pending} color="#9e9e9e" />

      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        <StatusChip label="Passed" count={passed} color="#2e7d32" icon={<PassedIcon sx={{ fontSize: 14 }} />} />
        <StatusChip label="Failed" count={failed} color="#d32f2f" icon={<FailedIcon sx={{ fontSize: 14 }} />} />
        <StatusChip label="Blocked" count={blocked} color="#ed6c02" icon={<BlockedIcon sx={{ fontSize: 14 }} />} />
        <StatusChip label="Skipped" count={skipped} color="#9e9e9e" icon={<SkippedIcon sx={{ fontSize: 14 }} />} />
      </Box>
    </Box>
  );
};

/** Release readiness dashboard. */
const ReleaseReadinessCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const readiness = data.readiness || data;
  const score = readiness.readiness_score ?? readiness.score ?? 0;
  const verdict = readiness.verdict || (score >= 80 ? 'GO' : score >= 50 ? 'CONDITIONAL' : 'NO-GO');
  const totalPlans = readiness.total_test_plans ?? 0;
  const completedPlans = readiness.completed_test_plans ?? 0;
  const openBlockers = readiness.open_blockers ?? readiness.blockers ?? 0;
  const passRate = readiness.overall_pass_rate ?? 0;
  const coveragePct = readiness.requirements_coverage ?? 0;

  const verdictColor = verdict === 'GO' ? '#2e7d32' : verdict === 'CONDITIONAL' ? '#ed6c02' : '#d32f2f';

  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: verdictColor,
        borderRadius: 2,
        p: 2,
        my: 1,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReadinessIcon fontSize="small" sx={{ color: verdictColor }} />
          <Typography variant="body2" fontWeight={700}>
            Release Readiness
          </Typography>
        </Box>
        <Chip
          label={verdict}
          size="small"
          sx={{ bgcolor: verdictColor, color: 'white', fontWeight: 700, fontSize: '0.75rem' }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, justifyContent: 'center' }}>
        <CoverageGauge percentage={Math.round(score)} label="Readiness Score" />
        <CoverageGauge percentage={Math.round(passRate)} label="Pass Rate" />
        <CoverageGauge percentage={Math.round(coveragePct)} label="Req. Coverage" />
      </Box>

      <Divider sx={{ my: 1 }} />

      <MetricRow label="Test Plans" value={`${completedPlans}/${totalPlans} completed`} color="#1976d2" total={totalPlans} current={completedPlans} />
      <MetricRow
        label="Open Blockers"
        value={openBlockers}
        color={openBlockers > 0 ? '#d32f2f' : '#2e7d32'}
      />
    </Box>
  );
};

export default CoverageDashboardCard;
